
import { Mic, Music2, Upload, Drum } from 'lucide-react';
import { ProcessingLimitDisplay } from '@/components/ProcessingLimitDisplay';
import { parseSongInfo } from '@/utils/songUtils';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

interface SidebarNavigationProps {
  sessions: any[];
  acapellaSessions: any[];
  drumSessions: any[];
  activeSession: string | null;
  onSessionSelect: (sessionId: string | null, type?: 'stem' | 'acapella' | 'drum') => void;
  showStemSplitter?: boolean;
  showAcapella?: boolean;
  showDrumRemover?: boolean;
}

export const SidebarNavigation = ({ 
  sessions, 
  acapellaSessions,
  drumSessions,
  activeSession, 
  onSessionSelect,
  showStemSplitter = true,
  showAcapella = true,
  showDrumRemover = true,
}: SidebarNavigationProps) => {
  const isMobile = useIsMobile();
  const { open } = useSidebar();

  if (isMobile && !open) {
    return null;
  }

  return (
    <Sidebar className="bg-sidebar border-none">
      <SidebarHeader className="p-4">
        <div className="flex">
          <img 
            src="/lovable-uploads/7c2cb694-9ce5-46b4-8e24-19579fce26cc.png" 
            alt="AI Stem Splitter Logo" 
            className="h-6 object-contain"
          />
        </div>
      </SidebarHeader>
      <SidebarContent className="flex flex-col h-full">
        <SidebarMenu className="flex-1 space-y-2 px-4">
          <Accordion type="multiple" defaultValue={["stems", "acapella", "drums"]} className="w-full">
            {showStemSplitter && (
              <AccordionItem value="stems" className="border-none">
                <AccordionTrigger className="py-2 hover:no-underline">
                  <div className="flex items-center gap-2">
                    <Music2 className="w-4 h-4" />
                    <span className="text-sm font-normal">AI Stem Splitter</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-2">
                  <div className="flex flex-col space-y-1">
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        onClick={() => onSessionSelect(null, 'stem')}
                        className={`w-full h-10 px-2 bg-background border border-border hover:bg-accent ${activeSession === null ? 'bg-accent text-accent-foreground' : ''}`}
                      >
                        <Upload className="w-4 h-4" />
                        <span>Upload audio</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    {sessions?.map((session) => (
                      <SidebarMenuItem key={session.id}>
                        <SidebarMenuButton
                          onClick={() => onSessionSelect(session.id, 'stem')}
                          className={`w-full h-10 px-2 ${session.id === activeSession ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'}`}
                        >
                          <Music2 className="w-4 h-4" />
                          <span>{parseSongInfo(session.original_filename).title}</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}
            {showAcapella && (
              <AccordionItem value="acapella" className="border-none">
                <AccordionTrigger className="py-2 hover:no-underline">
                  <div className="flex items-center gap-2">
                    <Mic className="w-4 h-4" />
                    <span className="text-sm font-normal">Acapella Extractor</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-2">
                  <div className="flex flex-col space-y-1">
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        onClick={() => onSessionSelect(null, 'acapella')}
                        className={`w-full h-10 px-2 bg-background border border-border hover:bg-accent ${activeSession === null ? 'bg-accent text-accent-foreground' : ''}`}
                      >
                        <Upload className="w-4 h-4" />
                        <span>Upload audio</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    {acapellaSessions?.map((session) => (
                      <SidebarMenuItem key={session.id}>
                        <SidebarMenuButton
                          onClick={() => onSessionSelect(session.id, 'acapella')}
                          className={`w-full h-10 px-2 ${session.id === activeSession ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'}`}
                        >
                          <Mic className="w-4 h-4" />
                          <span>{parseSongInfo(session.original_filename).title}</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}
            {showDrumRemover && (
              <AccordionItem value="drums" className="border-none">
                <AccordionTrigger className="py-2 hover:no-underline">
                  <div className="flex items-center gap-2">
                    <Drum className="w-4 h-4" />
                    <span className="text-sm font-normal">Drum Remover</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-2">
                  <div className="flex flex-col space-y-1">
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        onClick={() => onSessionSelect(null, 'drum')}
                        className={`w-full h-10 px-2 bg-background border border-border hover:bg-accent ${activeSession === null ? 'bg-accent text-accent-foreground' : ''}`}
                      >
                        <Upload className="w-4 h-4" />
                        <span>Upload audio</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    {drumSessions?.map((session) => (
                      <SidebarMenuItem key={session.id}>
                        <SidebarMenuButton
                          onClick={() => onSessionSelect(session.id, 'drum')}
                          className={`w-full h-10 px-2 ${session.id === activeSession ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'}`}
                        >
                          <Drum className="w-4 h-4" />
                          <span>{parseSongInfo(session.original_filename).title}</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}
          </Accordion>
        </SidebarMenu>
        <ProcessingLimitDisplay />
      </SidebarContent>
    </Sidebar>
  );
};
